import React, { useEffect, useCallback } from "react";

import _ from "lodash";
import PropTypes from "prop-types";
import { LogLevel } from '@bugfender/sdk';
import CartItem from "./CartItem";

import { prettyPrice, prettyTax, prettyPriceTotal } from "../utils";


const CartItems = ({
  tax,
  cart,
  user,
  Bugfender,
  handleRequest,
  cartState
}) => {
  const {
    items, couponCode, idsForDelay, expandCoupons, couponApplied,
    expandDelayed, expandReferral, showInvalidCoupon, disableCouponLoading,
    taxRequestPending, referralApplied, zipCode
  } = cartState;

  const updateTax = useCallback(() => {
    const requestAsync = async () => {
      try {
        handleRequest({
          type: 'SET_TAX_REQUEST_PENDING',
          payload: { taxRequestPending: true }
        });

        const subtotal = cart.total();

        if (tax.zipcode && subtotal && subtotal > 0) { // check your input arguments
          const salesTax = await cart.calculateTax(tax.zipcode, subtotal);

          tax.amount = salesTax;
          Bugfender.sendLog({
            level: LogLevel.Info,
            tag: "CartItems",
            text: `Tax calculated successfully: ${salesTax}`
          });
        } else {
          tax.amount = 0;
          Bugfender.sendLog({
            level: LogLevel.Warning,
            tag: "CartItems",
            text: 'Tax calculation skipped due to missing zipcode or subtotal'
          });
        }
      } catch (e) {
        Bugfender.sendLog({
          level: LogLevel.Error,
          tag: "CartItems",
          text: `Tax calculation failed: ${e.message}`
        });
        console.log(e);
      } finally {
        handleRequest({
          type: 'SET_TAX_REQUEST_PENDING',
          payload: { taxRequestPending: false }
        });
      }
    };

    requestAsync();
  }, [tax, cart]);

  const removeFromCart = useCallback((item) => {
    const requestAsync = async () => {
      try {
        const updatedItems = await item.destroy();

        _.each(updatedItems, function(updatedItem) {
          if (updatedItem.removed) {
            cart.removeCartItem(updatedItem.id);
          } else {
            item.primaryCartItemId = null;
          }
        });

        handleRequest({
          type: 'SET_ITEMS', payload: { items: items.filter(cartItem => cartItem.id !== item.id) }
        });
        updateTax();
        Bugfender.sendLog({
          level: LogLevel.Info,
          tag: "CartItems",
          text: `Item removed from cart: ${item.id}`
        });
      } catch (e) {
        Bugfender.sendLog({
          level: LogLevel.Error,
          tag: "CartItems",
          text: `Failed to remove item from cart: ${e.message}`
        });
        console.log(e);
      } finally {
        cart.sync();
      }
    };

    requestAsync();
  }, [tax, cart, items]);

  const handleTaxChangeEvent = useCallback((event) => {
    tax.zipcode = event.target.value;
    Bugfender.sendLog({
      level: LogLevel.Info,
      tag: "CartItems",
      text: `Tax zipcode changed: ${event.target.value}`
    });
  }, [tax]);

  const handleReferralEmail = useCallback((event) => {
    event.stopPropagation();

    cart.referral.email = event.target.value;
    Bugfender.sendLog({
      level: LogLevel.Info,
      tag: "CartItems",
      text: `Referral email changed: ${event.target.value}`
    });
  }, [cart]);

  const addReferralEmail = useCallback((event) => {
    const requestAsync = async () => {
      try {
        await cart.referral.validate();

        updateTax();
        Bugfender.sendLog({
          level: LogLevel.Info,
          tag: "CartItems",
          text: 'Referral email validated successfully'
        });
      } catch (e) {
        Bugfender.sendLog({
          level: LogLevel.Error,
          tag: "CartItems",
          text: `Failed to validate referral email: ${e.message}`
        });
        console.log(e);
      }
    };

    event.stopPropagation();

    requestAsync();
  }, [tax, cart]);

  const finishCouponLoading = useCallback(() => {
    handleRequest({
      type: 'SET_COUPON_LOADING',
      payload: { disableCouponLoading: false }
    });
    updateTax();
    Bugfender.sendLog({
      level: LogLevel.Info,
      tag: "CartItems",
      text: 'Coupon loading finished'
    });
  }, [tax, cart]);

  const confirmDelayed = useCallback(() => {
    const requestAsync = async () => {
      try {
        _.each(items, function(item) {
          item.delayed = _.includes(idsForDelay, item.id);
        });

        cart.items = [...items];

        handleRequest({
          type: 'SET_ITEMS', payload: { items: [...items] }
        });
        Bugfender.sendLog({
          level: LogLevel.Info,
          tag: "CartItems",
          text: `Items delayed: ${idsForDelay}`
        });
      } catch (e) {
        Bugfender.sendLog({
          level: LogLevel.Error,
          tag: "CartItems",
          text: `Failed to delay items: ${e.message}`
        });
        console.log(e);
      }
    };

    requestAsync();
  }, [cart, items, idsForDelay]);

  const handleDelayForItem = useCallback((item) => {
    if (idsForDelay.includes(item.id)) {
      handleRequest({
        type: 'SET_IDS_FOR_DELAY',
        payload: {
          idsForDelay: idsForDelay.filter(id => id !== item.id)
        }
      });
    } else {
      handleRequest({
        type: 'SET_IDS_FOR_DELAY',
        payload: {
          idsForDelay: [
            ...idsForDelay, item.id
          ]
        }
      });
    }
    Bugfender.sendLog({
      level: LogLevel.Info,
      tag: "CartItems",
      text: `Delay status changed for item: ${item.id}, delayed: ${idsForDelay.includes(item.id)}`
    });
  }, [idsForDelay]);

  const checkAndApplyCoupon = useCallback((event) => {
    const requestAsync = async () => {
      try {
        handleRequest({
          type: 'SET_COUPON_LOADING',
          payload: { disableCouponLoading: true }
        });
        cart.coupon.code = couponCode.trim();

        await cart.sync();

        if (cart.coupon.invalid) {
          handleRequest({
            type: 'UPDATE_STATE',
            payload: { showInvalidCoupon: true, couponApplied: false }
          });
          Bugfender.sendLog({
            level: LogLevel.Warning,
            tag: "CartItems",
            text: `Invalid coupon code applied: ${couponCode}`
          });
        } else {
          handleRequest({
            type: 'UPDATE_STATE',
            payload: { showInvalidCoupon: false, couponApplied: true }
          });
          Bugfender.sendLog({
            level: LogLevel.Info,
            tag: "CartItems",
            text: `Coupon applied successfully: ${couponCode}`
          });
        }
      } catch (e) {
        Bugfender.sendLog({
          level: LogLevel.Error,
          tag: "CartItems",
          text: `Failed to apply coupon: ${e.message}`
        });
        console.log(e);
      } finally {
        finishCouponLoading();
      }
    };

    event.stopPropagation();

    requestAsync();
  }, [tax, cart, couponCode]);

  useEffect(() => {
    if (!cart) {
      Bugfender.sendLog({
        level: LogLevel.Warning,
        tag: "CartItems",
        text: 'Cart is empty'
      });
      return;
    }

    handleRequest({
      type: 'SET_ITEMS', payload: { items: cart.items }
    });
    Bugfender.sendLog({
      level: LogLevel.Debug,
      tag: "CartItems",
      text: `Cart items set: ${cart.items}`
    });
  }, [cart]);

  useEffect(() => {
    updateTax();
  }, [zipCode]);

  if (!user || !cart.ready || !tax) {
    return (
      <div className="loading">
        <img alt="loading" src="/loader.gif" />
      </div>
    );
  }

  const handleOnClick = (event, payload) => {
    event.stopPropagation();
    handleRequest({ type: 'UPDATE_STATE', payload: payload });
  };

  const handleToggleKeyDown = (event, payload) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOnClick(event, payload);
    }
  };

  const handleActionsKeyDown = (event, callback, item = null) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      if (callback) callback(item ?? event);
    }
  };

  const cartTotal = prettyPrice(cart.total());
  const isDelayedButtonDisabled = idsForDelay.length === 0 && !items.some(item => item.delayed);

  return (
    <>
      {
        items.length === 0 && (
          <div className="empty-cart">
            <h1>You have no items in your cart.</h1>
            {
              !user.signed_in && (
                <div style={{ marginBottom: 10 }}>
                  <a href="/users/sign_in/">Log in to see saved cart items.</a>
                </div>
              )
            }
            <a className="empty" href="/">Return to homepage</a>
          </div>
        )
      }
      {
        items.length > 0 && (
          <div className="cart-items">
            <h1>In your cart ({ items.length } item{items.length > 1 ? "s" : ""})</h1>

            <div className="cart-summary">
              {
                items.map(item => <CartItem key={item.id} item={item} couponApplied={couponApplied} removeFromCart={removeFromCart} />)
              }

              {
                cart.referral.valid && (
                  <div className="cart-item clearfix">
                    <h2 className="name">Referral Discount</h2>

                    <span className="price red">-$10</span><br />
                  </div>
                )
              }

              <div className="summary-total">
                <h1 className="">Subtotal</h1>
                <h1 className="pull-right price-subtotal">${ cartTotal }</h1><br />
              </div>

              <div className="summary-total tax">
                <h1 className="">Estimated Sales Tax</h1>
                <span className="international-zipcode-bubble">
                  <span className="Tooltip hoverable">
                    ?
                    <div className="Tooltip-content">
                      For our international customers, you can proceed with your purchase by entering "00000" as your zip code.
                    </div>
                  </span>
                </span>

                <span className="zip-code-calculate">
                  <h1 className="billing-zip-code">
                    Billing Zip Code <input readOnly={!tax.enableCheckoutZip} value={tax.zipcode} onChange={handleTaxChangeEvent} className="billing-zip-code-input" />
                  </h1>
                  <h1 className="calculate-sales-tax">
                    <button type="button" id="updateTax" onClick={updateTax}>
                      {!taxRequestPending && <span id="calculate">Calculate</span>}
                      {taxRequestPending && <img alt="wait-time" id="zip-wait-image" src="/images/zip-lookup-wait.gif" />}
                    </button>
                  </h1>
                </span>
                <h1 className="pull-right price-total">
                  <span id="estimated-sales-tax">${ prettyTax(tax.amount).wholeDollarAmount }.
                    <sup>
                      <span>{ prettyTax(tax.amount).remainderAmount }</span>
                    </sup>
                  </span>
                </h1>
              </div>

              <div className="summary-total">
                <h1 className="">
                  Total ({items.length} Item{items.length > 1 ? "s" : ""})
                </h1>
                <h1 className="pull-right final-price">
                  <span className="price-total">${ prettyPriceTotal(cart.totalWithTax(tax.amount)).wholeDollarAmount }.
                    <sup>
                      <span>{ prettyPriceTotal(cart.totalWithTax(tax.amount)).remainderAmount }</span>
                    </sup>
                  </span>
                </h1><br />
                {cart.amountSaved() > 0 && (
                  <div>
                    <span className="price-saved-copy">You saved</span>
                    <span className="pull-right price-saving">${ prettyPrice(cart.amountSaved()) }</span>
                  </div>
                )}
              </div>

              {
                cart.canAcceptReferral() && (
                  <div
                    role="button"
                    tabIndex={0}
                    className="cart-ui clearfix referral"
                    onClick={() => handleRequest({ type: 'UPDATE_STATE', payload: { expandReferral: !expandReferral } })}
                    onKeyDown={event => handleToggleKeyDown(event, { expandReferral: !expandReferral })}
                  >
                    {cart.referral.valid && !user.referredById && <span className="green">Referral successfully applied</span>}

                    {
                      !cart.referral.valid && (
                        <div>
                          Referral email?

                          {expandReferral && (
                            <div className="input-toggle">
                              {!referralApplied && <input onClick={event => event.stopPropagation()} onChange={handleReferralEmail} type="email" name="referral-email" disabled={user.referredById} placeholder="Enter referral email" />}
                              {!referralApplied && (
                                <button
                                  type="button"
                                  id="apply-referral"
                                  className="btn btn-orange inactive"
                                  disabled={user.referredById}
                                  onClick={addReferralEmail}
                                  onKeyDown={event => handleActionsKeyDown(event, addReferralEmail)}
                                >Apply
                                </button>
                              )}
                              <span className="clearfix" />
                              {cart.referral.failed && <p className="red pull-right">Unable to apply referral</p>}
                              {user.referredById && <p className="red pull-right">Referral already used</p>}
                            </div>
                          )}
                          <span className="clearfix" />
                        </div>
                      )}
                  </div>
                )
              }

              {cart.canAcceptPromo() && (
                <div
                  role="button"
                  tabIndex={0}
                  className="cart-ui clearfix promocode"
                  onClick={event => handleOnClick(event, { expandCoupons: !expandCoupons })}
                  onKeyDown={event => handleToggleKeyDown(event, { expandCoupons: !expandCoupons })}
                >

                  Promo code?

                  {expandCoupons && (
                    <div className="input-toggle">
                      <input type="text" onClick={event => event.stopPropagation()} onChange={event => handleRequest({ type: "SET_COUPON_CODE", payload: { couponCode: event.target.value } })} disabled={disableCouponLoading} placeholder="Enter promo code" name="promocode" />
                      <button
                        type="button"
                        className="btn btn-orange inactive"
                        disabled={disableCouponLoading || !couponCode}
                        onClick={checkAndApplyCoupon}
                        onKeyDown={event => handleActionsKeyDown(event, checkAndApplyCoupon)}
                      > Apply
                      </button>
                      <span className="clearfix" />
                      {showInvalidCoupon && <p className="red invalid-code pull-right">Invalid promo code</p>}
                      <span className="clearfix" />
                      {couponApplied && <p className="pull-right">Coupon applied to eligible products</p>}
                    </div>
                  )}
                </div>
              )}
            </div>

            {cart.hasAnyDelayableProducts() && (
              <div
                role="button"
                tabIndex={0}
                className="cart-ui clearfix delay"
                onClick={event => handleOnClick(event, { expandDelayed: !expandDelayed })}
                onKeyDown={event => handleToggleKeyDown(event, { expandDelayed: !expandDelayed })}
              >
                <p>Click to <span className="bold">delay</span> your Question Bank start date</p>

                {expandDelayed && (
                  <div role="button" tabIndex={0} className="input-toggle" onClick={event => event.stopPropagation()} onKeyPress={() => {}}>
                    <p>You can delay the start date of any 3 or 6 month plan for
                      up to 6 months. When you're ready to begin, simply
                      activate the question bank from your dashboard to start.
                    </p>

                    {
                      items.map((item) => {
                        if (item.product.isEligibleForDelay()) {
                          return (
                            <div key={`delay-item-${item.id}`} className="checkbox-choice clearfix">
                              <input
                                type="checkbox"
                                id={`delay-item-${item.id}`}
                                value={item.id}
                                checked={idsForDelay.includes(item.id)}
                                onChange={() => handleDelayForItem(item)}
                                onKeyDown={event => handleActionsKeyDown(event, handleDelayForItem, item)}
                              />
                              <label className="selection" htmlFor={`delay-item-${item.id}`}>
                                <h4>{ item.product.displayName }</h4>
                              </label>
                            </div>
                          );
                        }

                        return null;
                      })
                    }
                    <button
                      type="button"
                      className={`btn btn-orange ${idsForDelay.length === 0 ? "inactive" : ""}`}
                      disabled={isDelayedButtonDisabled}
                      onClick={confirmDelayed}
                      onKeyDown={event => handleActionsKeyDown(event, confirmDelayed)}
                    > Delay Question Bank
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      }

      <div className="modal" id="cme-purchase-restricted-modal">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title" style={{ display: "inline-block" }}>Sorry, you have claimed CME for this product already.</h4>
              <button type="button" className="close" data-dismiss="modal">&times;</button>
            </div>

            <div className="modal-body" style={{ backgroundColor: "#ecf5fb" }}>
              <p>Our records show you have previously claimed CME for this activity. Unfortunately, you are ineligible to purchase this CME content again.</p>

              <p>We have removed this from your cart.</p>

              <p>Please select another specialty or visit
                <a href="/cme"> CME Coach</a> to browse
                our other CME offerings that meet your requirements.
              </p>

              <div className="center" style={{ textAlign: "center" }}>
                <p><b>Need assistance? Reach out to us at</b></p>
                <p><a href="mailto:support@boardvitals.com"><b>support@boardvitals.com</b></a></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

CartItems.propTypes = {
  tax: PropTypes.shape({
    zipcode: PropTypes.string,
    amount: PropTypes.number,
    enableCheckoutZip: PropTypes.bool,
  }).isRequired,
  cart: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  Bugfender: PropTypes.object.isRequired,
  handleRequest: PropTypes.func.isRequired,
  cartState: PropTypes.shape({
    items: PropTypes.array.isRequired,
    couponCode: PropTypes.string,
    idsForDelay: PropTypes.array,
    expandCoupons: PropTypes.bool,
    couponApplied: PropTypes.bool,
    expandDelayed: PropTypes.bool,
    expandReferral: PropTypes.bool,
    showInvalidCoupon: PropTypes.bool,
    disableCouponLoading: PropTypes.bool,
    taxRequestPending: PropTypes.bool,
    referralApplied: PropTypes.bool,
    zipCode: PropTypes.string,
  }).isRequired,
};

export default CartItems;
